<template>
  <v-menu
    v-model="menu"
    :close-on-content-click="false"
    :nudge-width="220"
    offset-y
    content-class="scf__menu">
    <template #activator="{ attrs, on }">
      <v-btn
        v-bind="attrs"
        v-on="on"
        outlined
        class="scf__trigger"
        :aria-label="ariaLabel">
        <span class="scf__swatch" :style="{ backgroundColor: swatch }"></span>
        <span class="scf__hex">{{ swatch }}</span>
        <v-icon small>mdi-palette-outline</v-icon>
      </v-btn>
    </template>

    <v-card class="scf__card">
      <v-card-text class="scf__card-text">
        <v-color-picker
          v-if="menu"
          class="hue-color-picker"
          :value="swatch"
          flat
          mode="hexa"
          hide-mode-switch
          show-swatches
          :swatches="swatches"
          :swatches-max-height="180"
          @input="onInput">
        </v-color-picker>
        <span class="scf__hint">{{ hint }}</span>
      </v-card-text>
    </v-card>
  </v-menu>
</template>

<script>
// Shared color popover for the schema editors (bands + entries). Body-attached (default `attach`)
// so it escapes a scrollable dialog's overflow and Vuetify auto-flips it upward near the bottom
// edge; the app-standard v-color-picker (mode="hexa", editable hex, swatches — mirrors
// Share/ColorPickerButton.vue) replaces the earlier clipped, chrome-less hand-rolled square.
import { NEUTRAL_SWATCH } from '@/features/Dashboard/utils/coloringSchemas';

// Design-language swatches — EXACT hexes already used by this feature so an authored color can
// reproduce chart output: TONE_FILL / gauge tones (echartsOptions.js:159-162, gaugeOption.js:12-14)
// and the raw category hues (coloringSchemas.js:109-114); steel-blue is ColorPickerButton's default.
const DESIGN_SWATCHES = Object.freeze([
  ['#03624C', '#2CC295'], // castleton (good light) / mint (good dark)
  ['#A07A00', '#D2A000'], // olive (caution light) / gold (caution dark)
  ['#D97706', '#FF9800'], // severity-high orange / re-inspect orange
  ['#B71C1C', '#EF5350'], // critical red (critical light) / red (critical dark)
  ['#9C27B0', '#547D9A'], // FFS purple / steel blue
  ['#3F564D', '#9E9E9E'], // slate / neutral grey
]);

export default {
  name: 'SchemaColorField',

  props: {
    // Stored color as a {light, dark} pair (or null). The picker authors a single hue, so it emits
    // a same-hex pair — matching how the built-in raw colors (e.g. SEVERITY_HIGH_COLOR) are shaped.
    value: { type: Object, default: null },
    ariaLabel: { type: String, default: 'Choose color' },
    hint: { type: String, default: 'Choose any color.' },
    swatches: { type: Array, default: () => DESIGN_SWATCHES },
  },

  data() {
    return { menu: false };
  },

  computed: {
    swatch() {
      return (this.value && this.value.light) ? this.value.light : NEUTRAL_SWATCH;
    },
  },

  methods: {
    // v-color-picker (mode="hexa") emits a string or object; take the 6-digit hex, drop any alpha.
    normalizedHex(value) {
      const candidate = typeof value === 'string' ? value : (value && (value.hex || value.hexa));
      const hex = (candidate || '').substring(0, 7);
      return /^#[0-9a-f]{6}$/i.test(hex) ? hex.toUpperCase() : null;
    },

    onInput(value) {
      const hex = this.normalizedHex(value);
      if (!hex) return;
      this.$emit('input', { light: hex, dark: hex });
    },
  },
};
</script>

<style lang="scss" scoped>
.scf__trigger { width: 100%; min-width: 0 !important; height: 40px !important; justify-content: flex-start; padding: 0 8px !important; text-transform: none !important; letter-spacing: normal !important; }
.scf__swatch { display: inline-block; flex: 0 0 auto; width: 16px; height: 16px; margin-right: 7px; border: 1px solid rgba(0, 0, 0, 0.2); border-radius: 4px; }
.scf__hex { flex: 1; overflow: hidden; color: var(--v-text-base); font-size: 12.5px; font-weight: 500; text-align: left; text-overflow: ellipsis; white-space: nowrap; }
.scf__trigger:focus-visible { outline: 2px solid var(--ds-focus-ring-color, #2cc295); outline-offset: 2px; }
.scf__card { width: 288px; }
.scf__card-text { padding: 8px !important; }
.scf__hint { display: block; padding: 2px 4px 4px; color: #52615c; font-size: 12px; font-weight: 500; line-height: 1.4; }
.theme--dark .scf__swatch { border-color: rgba(255, 255, 255, 0.24); }
.theme--dark .scf__hint { color: rgba(255, 255, 255, 0.78); }
</style>

<!-- Un-scoped: the teleported menu content lives outside this component's root, so scoped rules
     can't reach it. z-index 2100 (the app's overlay-menu convention — matches the ~8 monitoring
     dialogs' `menu-props.zIndex`) keeps the popover above the dialog; !important beats Vuetify's
     inline z-index. Hide the alpha channel (we author opaque hues) — same as ColorPickerButton. -->
<style lang="scss">
.scf__menu { z-index: 2100 !important; }
.hue-color-picker .v-color-picker__alpha { display: none; }
</style>
